import { useMemo, useState } from 'react';
import { addDays, isSameDay, format, startOfDay } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import { CalendarDays } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

type DateSelectorProps = {
  selectedDate: Date;
  onSelect: (date: Date) => void;
  /**
   * Anchor for the strip's leftmost day. Computed in the parent as a
   * pure function of `(selectedDate, today)` — see
   * `AvailabilityPage`. Lets the strip re-anchor around a far-out
   * selection while keeping `DateSelector` purely presentational.
   */
  anchorDate: Date;
  /**
   * Customer's effective max-advance window in days, sourced from the
   * booking-policy endpoint + VIP status in the parent. Used by the
   * calendar popover to gray out dates beyond the horizon.
   */
  effectiveMaxAdvanceDays: number;
  /**
   * Number of day pills rendered in the strip itself. Anything beyond
   * the strip is reachable via the calendar popover. Default 7 —
   * matches the "next week + calendar for everything else" pattern.
   */
  stripDays?: number;
};

export default function DateSelector({
  selectedDate,
  onSelect,
  anchorDate,
  effectiveMaxAdvanceDays,
  stripDays = 7,
}: DateSelectorProps) {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === 'fr' ? fr : undefined;
  const [popoverOpen, setPopoverOpen] = useState(false);

  // Strip pills — anchored on whatever the parent passed in (today
  // for the common case; selectedDate when it falls outside today's
  // first week). startOfDay normalises so time-of-day differences
  // between today and anchorDate don't produce duplicate pills.
  const days = useMemo(() => {
    const result: Date[] = [];
    const start = startOfDay(anchorDate);
    for (let i = 0; i < stripDays; i++) {
      result.push(addDays(start, i));
    }
    return result;
  }, [anchorDate, stripDays]);

  // Calendar popover horizon — today + effective window. Past dates
  // and dates beyond the window are grayed out by react-day-picker.
  const today = useMemo(() => startOfDay(new Date()), []);
  const maxBookableDate = useMemo(
    () => addDays(today, effectiveMaxAdvanceDays),
    [today, effectiveMaxAdvanceDays],
  );

  // Responsive justification: below md the 7-day strip is wide enough
  // (especially under the FR locale where day labels like `Jeu.` add
  // ~1 char per pill) to overflow narrow viewports — and `justify-center`
  // then distributes the overflow symmetrically, clipping the leftmost
  // pill's leading character off the left edge. `justify-start` keeps
  // today flush-left with overflow reachable via the natural horizontal
  // scroll. At md+ the strip fits comfortably and we restore the
  // centered visual balance with the bay grid below.
  return (
    <div className="flex justify-start md:justify-center items-center gap-1 overflow-x-auto pb-2 scrollbar-hide">
      {days.map((day) => {
        const isSelected = isSameDay(day, selectedDate);
        const isToday = isSameDay(day, today);

        return (
          <button
            key={day.toISOString()}
            type="button"
            onClick={() => onSelect(day)}
            className={cn(
              'flex flex-col items-center px-3 py-2 rounded-lg min-w-[60px] text-sm transition-colors min-w-[56px] text-center',
              isSelected
                ? 'bg-primary text-primary-foreground font-medium'
                : 'hover:bg-muted',
            )}
          >
            <span className="text-xs uppercase">{format(day, 'EEE', { locale: dateLocale })}</span>
            <span className="text-lg font-semibold">{format(day, 'd')}</span>
            <span className="text-xs">{format(day, 'MMM', { locale: dateLocale })}</span>
            {isToday && !isSelected && (
              <div className="w-1.5 h-1.5 rounded-full bg-primary mt-0.5" />
            )}
          </button>
        );
      })}

      <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
        <PopoverTrigger
          render={(props) => (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={t('booking.open_date_picker')}
              {...props}
            >
              <CalendarDays className="h-5 w-5" />
            </Button>
          )}
        />
        <PopoverContent align="end" className="w-auto p-0">
          <Calendar
            mode="single"
            locale={dateLocale}
            selected={selectedDate}
            onSelect={(d) => {
              if (d) {
                onSelect(d);
                setPopoverOpen(false);
              }
            }}
            disabled={{ before: today, after: maxBookableDate }}
            autoFocus
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
